/**
 * attendanceAdapter.js
 *
 * Transforms tidsregistering domain objects into the Category[] shape
 * expected by AttendanceChart (two-track, hour-granularity, single-day view).
 *
 * Input:
 *   plannedShifts     — PlannedShift[] for the selected date (all staff in dept)
 *   dayOverviews      — DayOverview[] each containing time_details: TimeDetail[]
 *   selectedDate      — string 'yyyy-MM-dd'
 *   registrationCodes — RegistrationCode[]
 *   staffMembers      — StaffMember[]
 *
 * Output: Category[] where each Category is one staff-member row.
 *   Each row has:
 *     shiftSlots   — PlannedShift bars (top track, planned hours)
 *     detailSlots  — TimeDetail bars (bottom track, actual registrations)
 *     tasks        — shiftSlots + detailSlots combined (for layout height calculations)
 */

import { CATEGORY_COLORS, CODE_COLORS, REGISTRATION_CATEGORY_COLORS, DEFAULT_COLOR, SHIFT_COLOR } from '../constants/colors';

function resolveCodeColor(registrationCode) {
  if (!registrationCode) return DEFAULT_COLOR;
  return REGISTRATION_CATEGORY_COLORS[registrationCode.registration_category]
    ?? CATEGORY_COLORS[registrationCode.statistical_category]
    ?? CODE_COLORS[registrationCode.work_time_type]
    ?? DEFAULT_COLOR;
}

/**
 * Builds a full Date from a date string + time string ('HH:MM[:SS]').
 * Returns null if time is null/undefined.
 */
function toDateTime(dateStr, timeStr) {
  if (!timeStr) return null;
  return new Date(`${dateStr}T${timeStr}`);
}

/**
 * Computes deviation between actual registered minutes and scheduled minutes.
 */
function computeDeviation(dayOverview) {
  if (!dayOverview) return { deltaMinutes: 0, severity: 0 };
  return {
    deltaMinutes: Math.round((dayOverview.hours_diff_from_scheduled ?? 0) * 60),
    severity: dayOverview.severity_code ?? 0,
  };
}

/**
 * @param {object[]} plannedShifts    — PlannedShift[] for the selected date
 * @param {object[]} dayOverviews     — DayOverview[] with time_details
 * @param {string}   selectedDate     — 'yyyy-MM-dd'
 * @param {object[]} registrationCodes
 * @param {object[]} staffMembers
 * @returns {object[]} Category[]
 */
export function buildAttendanceCategories(
  plannedShifts,
  dayOverviews,
  selectedDate,
  registrationCodes,
  staffMembers,
  rawRegistrations = [],
  staffOverenskomstMap = {},
) {
  const now = new Date();
  const codeMap = new Map(registrationCodes.map((rc) => [rc.registration_code_id, rc]));
  const overviewByStaff = new Map(dayOverviews.map((d) => [d.staff_member_id, d]));

  // Group raw registrations by staff member
  const regsByStaff = new Map();
  for (const reg of rawRegistrations) {
    const list = regsByStaff.get(reg.staff_member_id) ?? [];
    list.push(reg);
    regsByStaff.set(reg.staff_member_id, list);
  }

  // Group planned shifts by staff_member_id (multiple shifts per staff allowed)
  const shiftsByStaff = new Map();
  for (const shift of plannedShifts) {
    const list = shiftsByStaff.get(shift.staff_member_id) ?? [];
    list.push(shift);
    shiftsByStaff.set(shift.staff_member_id, list);
  }

  return staffMembers.map((staffMember) => {
    const sid = staffMember.staff_member_id;
    const dayOverview = overviewByStaff.get(sid) ?? null;
    const staffShifts = shiftsByStaff.get(sid) ?? [];
    const deviation = computeDeviation(dayOverview);

    // Top track: planned shift bars (isShift: true)
    const shiftSlots = staffShifts
      .filter((s) => s.is_working)
      .map((shift) => ({
        id: `shift-${shift.planned_shift_id}`,
        startDate: toDateTime(selectedDate, shift.start_time),
        endDate: toDateTime(selectedDate, shift.end_time),
        color: SHIFT_COLOR,
        isShift: true,
        isBackground: false,
        plannedShift: shift,
      }))
      .filter((s) => s.startDate && s.endDate);

    // Bottom track: time detail bars from day overview (completed registrations)
    const timeDetails = dayOverview?.time_details ?? [];
    const detailSlots = timeDetails
      .map((detail) => {
        const registrationCode = codeMap.get(detail.registration_code_id) ?? null;
        const start = toDateTime(selectedDate, detail.start_time);
        const end = toDateTime(selectedDate, detail.end_time);
        if (!start || !end) return null;
        return {
          id: detail.time_detail_id,
          startDate: start,
          endDate: end,
          color: resolveCodeColor(registrationCode),
          isShift: false,
          isBackground: false,
          registrationCode,
          originalDetail: detail,
        };
      })
      .filter(Boolean);

    // Live bars: open registrations (clocked in but not yet out) extend to now
    const staffRegs = regsByStaff.get(sid) ?? [];
    const liveSlots = staffRegs
      .filter((reg) => reg.start_logged && !reg.end_logged)
      .map((reg) => {
        const registrationCode = codeMap.get(reg.registration_code_id) ?? null;
        const start = toDateTime(selectedDate, reg.start_time);
        if (!start) return null;
        return {
          id: `live-${reg.registration_id}`,
          startDate: start,
          endDate: now,
          color: resolveCodeColor(registrationCode) ,
          isShift: false,
          isBackground: false,
          isLive: true,
          registrationCode,
          originalRegistration: reg,
        };
      })
      .filter(Boolean);

    const allDetailSlots = [...detailSlots, ...liveSlots];

    const overenskomstId = staffOverenskomstMap[sid]?.overenskomst_id ?? null;
    const overenskomstName = staffOverenskomstMap[sid]?.overenskomst_name ?? null;

    return {
      id: sid,
      name: staffMember.name,
      staffMember,
      deviation,
      dayOverview,
      shiftSlots,
      detailSlots: allDetailSlots,
      tasks: [...shiftSlots, ...allDetailSlots],
      overenskomstId,
      overenskomstName,
    };
  });
}

/**
 * Wraps a flat Category[] with group-header sentinel rows, one per overenskomst.
 * Staff without an overenskomst are grouped under the given fallbackLabel.
 */
export function groupCategoriesByOverenskomst(categories, fallbackLabel = 'Ukendt overenskomst') {
  const groups = new Map();

  for (const cat of categories) {
    const key = cat.overenskomstId ?? '__none__';
    const label = cat.overenskomstName ?? fallbackLabel;
    if (!groups.has(key)) groups.set(key, { label, members: [] });
    groups.get(key).members.push(cat);
  }

  const sorted = Array.from(groups.entries()).sort(([keyA, a], [keyB, b]) => {
    if (keyA === '__none__') return 1;
    if (keyB === '__none__') return -1;
    return a.label.localeCompare(b.label, 'da');
  });

  const result = [];
  for (const [key, { label, members }] of sorted) {
    result.push({
      id: `__group__${key}`,
      name: label,
      isGroupHeader: true,
      tasks: [],
    });
    result.push(...members.sort((a, b) => a.name.localeCompare(b.name, 'da')));
  }
  return result;
}

// Keep old export name as alias for any callers still using it
export const dayOverviewsToCategories = (dayOverviews, _schedules, selectedDate, registrationCodes, staffMembers) =>
  buildAttendanceCategories([], dayOverviews, selectedDate, registrationCodes, staffMembers);
